import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { impersonationApi, type ImpersonationCandidate } from '../api/impersonationApi';

const MIN_CHARS = 3;
const DEBOUNCE_MS = 300;

/**
 * Debounced user search for the impersonation picker.
 * Only queries the backend once at least 3 characters have been typed.
 */
export const useUserSearch = () => {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const handle = setTimeout(() => setDebounced(query.trim()), DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [query]);

  const enabled = debounced.length >= MIN_CHARS;

  const { data, isFetching, error } = useQuery({
    queryKey: ['impersonation-user-search', debounced],
    queryFn: () => impersonationApi.searchUsers(debounced),
    enabled,
    staleTime: 30_000,
  });

  const results: ImpersonationCandidate[] = enabled ? data ?? [] : [];

  return {
    query,
    setQuery,
    results,
    loading: enabled && isFetching,
    error: error instanceof Error ? error.message : null,
    hasEnoughChars: enabled,
    minChars: MIN_CHARS,
  };
};
